import {
  isCalendarDate,
  maximumAttestationDays,
  type ExternalAppearanceStatus,
  type ExternalAttestationStatus,
  type RecordExternalAppearanceRequest,
  type RecordExternalHistoryAttestationRequest,
  type WithdrawExternalHistoryRequest,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const appearanceStatusKeys: Readonly<Record<ExternalAppearanceStatus, TranslationKey>> = {
  recorded: 'externalHistory.appearance.status.recorded',
  withdrawn: 'externalHistory.appearance.status.withdrawn',
};

export const attestationStatusKeys: Readonly<Record<ExternalAttestationStatus, TranslationKey>> = {
  granted: 'externalHistory.attestation.status.granted',
  withdrawn: 'externalHistory.attestation.status.withdrawn',
};

export type ExternalHistoryKind = 'appearances' | 'attestations';

export function externalHistoryPath(
  base: string,
  record?: { kind: ExternalHistoryKind; id?: string; withdraw?: boolean },
): string {
  const root = `${base}/external-history`;
  if (record === undefined) return root;
  const collection = `${root}/${record.kind}`;
  if (record.id === undefined) return collection;
  const one = `${collection}/${encodeURIComponent(record.id)}`;
  return record.withdraw === true ? `${one}/withdraw` : one;
}

/** The list path of one athlete's records. */
export function externalHistoryListPath(base: string, athleteId: string): string {
  return `${externalHistoryPath(base)}?athleteId=${encodeURIComponent(athleteId)}`;
}

export interface ExternalAppearanceForm {
  playedOn: string;
  competitionName: string;
  sourceReference: string;
}

export const emptyAppearanceForm: ExternalAppearanceForm = {
  playedOn: '',
  competitionName: '',
  sourceReference: '',
};

/** The request for the form, or null while a field is empty or the day is not a real date. */
export function externalAppearanceRequest(
  athleteId: string,
  form: ExternalAppearanceForm,
): RecordExternalAppearanceRequest | null {
  const competitionName = form.competitionName.trim();
  const sourceReference = form.sourceReference.trim();
  if (
    athleteId === '' ||
    !isCalendarDate(form.playedOn) ||
    competitionName === '' ||
    sourceReference === ''
  ) {
    return null;
  }
  return { athleteId, playedOn: form.playedOn, competitionName, sourceReference };
}

export interface AttestationForm {
  coversFrom: string;
  coversThrough: string;
  statement: string;
}

export const emptyAttestationForm: AttestationForm = {
  coversFrom: '',
  coversThrough: '',
  statement: '',
};

function daysBetween(from: string, through: string): number {
  return (Date.parse(`${through}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000;
}

/**
 * The request for the form, or null while a field is empty, a day is not a real date, the last day
 * is before the first or the stretch is longer than the server accepts.
 */
export function attestationRequest(
  athleteId: string,
  form: AttestationForm,
): RecordExternalHistoryAttestationRequest | null {
  const statement = form.statement.trim();
  if (
    athleteId === '' ||
    statement === '' ||
    !isCalendarDate(form.coversFrom) ||
    !isCalendarDate(form.coversThrough) ||
    form.coversThrough < form.coversFrom ||
    daysBetween(form.coversFrom, form.coversThrough) > maximumAttestationDays
  ) {
    return null;
  }
  return {
    athleteId,
    coversFrom: form.coversFrom,
    coversThrough: form.coversThrough,
    statement,
  };
}

/** The withdrawal request, or null while no reason is typed. */
export function withdrawExternalHistoryRequest(
  reason: string,
): WithdrawExternalHistoryRequest | null {
  const trimmed = reason.trim();
  return trimmed === '' ? null : { reason: trimmed };
}
